import { City, CommonOptions } from '../interfaces/interfaces';

export class DocumentExtractionMapper {
  /**
   * Mapea el gentilicio devuelto por la extracción (ej. "COLOMBIANA") a la opción del catálogo Nationality.
   * Devuelve null si no hay coincidencia confiable en vez de inventar un valor.
   */
  static mapGentilicioToNationality(gentilicio: string | null, nationalityOptions: CommonOptions[]): string | null {
    if (!gentilicio || !nationalityOptions?.length) { return null; }
    const normalized = gentilicio.toString().trim().toUpperCase();
    const root = normalized.slice(0, Math.max(4, normalized.length - 2));
    const match = nationalityOptions.find(option => (option.Description || '').toUpperCase().includes(root));
    return match ? match.Value : null;
  }

  /**
   * Parsea "CIUDAD (DEPARTAMENTO)" en sus dos componentes. Devuelve null si el formato no coincide.
   */
  static parseLugarNacimiento(lugar: string | null): { city: string; department: string } | null {
    if (!lugar) { return null; }
    const match = lugar.match(/^(.*)\s*\((.*)\)\s*$/);
    if (!match) { return null; }
    return { city: match[1].trim(), department: match[2].trim() };
  }

  /**
   * Normaliza texto para comparación: quita tildes, puntuación (. ,) y espacios repetidos.
   * Así "BOGOTÁ D.C." y "BOGOTA D.C" se consideran equivalentes.
   */
  private static normalizeText(value: string | null | undefined): string {
    const decomposed = (value || '').toString().normalize('NFD');
    let stripped = '';
    for (let i = 0; i < decomposed.length; i++) {
      const code = decomposed.charCodeAt(i);
      // Rango Unicode de marcas diacríticas combinantes (acentos, tildes, etc. tras normalize('NFD'))
      if (code >= 0x0300 && code <= 0x036f) { continue; }
      stripped += decomposed[i];
    }
    return stripped
      .replace(/[.,]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();
  }

  /**
   * Busca coincidencia del nombre de ciudad dentro del catálogo de ciudades, ignorando tildes/puntuación.
   * No hace match aproximado por substring: si no hay coincidencia (normalizada), devuelve null.
   */
  static matchCityByName(cityName: string | null, cities: City[]): City | null {
    if (!cityName || !cities?.length) { return null; }
    const normalized = this.normalizeText(cityName);
    return cities.find(city => this.normalizeText(city.Name) === normalized) || null;
  }

  /**
   * Parsea una fecha "yyyy-MM-dd" (o con hora, ISO) como fecha LOCAL, evitando el corrimiento de
   * un día que ocurre al usar `new Date('yyyy-MM-dd')` (JS la interpreta como UTC medianoche).
   */
  static parseIsoDateLocal(value: string | null): Date | null {
    if (!value) { return null; }
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) { return null; }
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
}
